import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ExternalLink, RotateCw, Copy, Check } from 'lucide-react';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import MetaGrid from 'shared/components/ui/MetaGrid';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import usePolling from 'shared/hooks/usePolling';
import { ACTIVITY_DRAWER_PANEL_CLASS, activityToolbarButtonClass, drawerIconButtonClass } from 'features/activity/styles/dataTableStyles';
import { getExecutionLite, retryExecution, cancelExecution } from 'features/agents/api/agentsApi';
import { RunStatusDot, execStatusLabel, isRunActive } from 'features/agents/lib/status';
import { runTriggerLabel } from 'features/agents/lib/triggerMeta';
import { timeAgo, formatDate, formatExecDuration } from 'features/agents/lib/format';
import { normalize as normalizeDefinition } from 'features/agents/WorkflowEditor/definition';
import { FlowCanvas } from 'features/agents/canvas';
import StepTimeline, { errorText } from 'features/agents/runs/StepTimeline';
import JsonPane from 'features/agents/runs/JsonPane';
import { startedBy } from 'features/agents/runs/RunsTable';

const FINISHED = ['succeeded', 'failed', 'cancelled'];
const TABS = [
  { value: 'steps', label: 'Steps' },
  { value: 'graph', label: 'Graph' },
  { value: 'payload', label: 'Trigger payload' },
];

export const canRetry = (status, canWrite) => !!canWrite && FINISHED.includes(status);
export const canCancel = (status, canWrite) => !!canWrite && isRunActive(status);

/**
 * One run, beside the table. Loaded without step payloads; opening a step
 * fetches its input and output. Polls only while the run is going.
 */
export default function RunDrawer({ executionId, row, workflowName, canWrite, onClose, onChanged, onOpenAgent }) {
  const [exec, setExec] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('steps');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [nodeFocus, setNodeFocus] = useState(null);
  const closeRef = useRef(null);

  const load = useCallback(async (isActive = () => true) => {
    try {
      const e = await getExecutionLite(executionId);
      if (!isActive()) return false;
      setExec(e);
      setError('');
      return isRunActive(e?.status);
    } catch (err) {
      if (isActive()) setError(err?.response?.data?.error || err.message || 'Could not load this run');
      return undefined;
    }
  }, [executionId]);

  useEffect(() => { setExec(null); setTab('steps'); setNodeFocus(null); setError(''); }, [executionId]);
  usePolling(load, { enabled: Boolean(executionId), interval: 3000, maxInterval: 15000, key: executionId });

  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);

  const run = exec || row || {};
  const status = exec?.status || row?.status;
  const definition = useMemo(() => (exec?.definition ? normalizeDefinition(exec.definition) : null), [exec?.definition]);
  const stepByNodeId = useMemo(() => {
    const m = {};
    for (const s of (exec?.steps || [])) m[s.node_id] = s;
    return m;
  }, [exec?.steps]);

  const act = async (kind) => {
    setBusy(kind);
    setError('');
    try {
      if (kind === 'retry') {
        const next = await retryExecution(executionId);
        await onChanged?.(next);
      } else {
        await cancelExecution(executionId);
        await load();
        await onChanged?.(null);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Something went wrong');
    } finally { setBusy(''); }
  };

  const copyId = () => {
    try { navigator.clipboard.writeText(executionId); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch (_) { /* denied */ }
  };

  const cells = [
    { label: 'Status', content: <span className="inline-flex items-center gap-1.5"><RunStatusDot status={status} />{execStatusLabel(status)}</span> },
    { label: 'Agent', content: workflowName || run.workflow_name || '—', title: workflowName || run.workflow_name || undefined },
    { label: 'Version', content: run.version_number != null ? `v${run.version_number}` : '—' },
    { label: 'Trigger', content: runTriggerLabel(run) || '—', title: runTriggerLabel(run) || undefined },
    { label: 'Started', content: timeAgo(run.started_at) || '—', title: formatDate(run.started_at) || undefined },
    { label: 'Duration', content: formatExecDuration(run.duration_ms) || (isRunActive(status) ? 'Running' : '—') },
    { label: 'Triggered by', content: startedBy(run) },
  ];
  const failure = status === 'failed' ? errorText(exec?.error || (run.error_message ? { message: run.error_message } : null)) : '';

  return (
    <div className={DRAWER_OVERLAY_CLASS}>
      <DrawerScrim />
      <aside aria-label="Run" className={ACTIVITY_DRAWER_PANEL_CLASS} style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex h-12 shrink-0 items-center gap-2 px-4">
          <RunStatusDot status={status} />
          <span className="shrink-0 text-[13px] font-medium text-[color:var(--text-primary)]">Run</span>
          <span className="min-w-0 truncate font-mono text-[12px] text-[color:var(--text-muted)]" title={executionId}>{String(executionId).slice(0, 8)}</span>
          <button type="button" onClick={copyId} aria-label="Copy run id" title="Copy run id" className={drawerIconButtonClass}>
            {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
          </button>
          <div className="flex-1" />
          {canRetry(status, canWrite) && (
            <button type="button" disabled={!!busy} onClick={() => act('retry')} className={activityToolbarButtonClass}>
              <RotateCw size={12} aria-hidden /> {busy === 'retry' ? 'Retrying…' : 'Retry'}
            </button>
          )}
          {canCancel(status, canWrite) && (
            <button type="button" disabled={!!busy} onClick={() => act('cancel')} className={activityToolbarButtonClass}>
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          <button type="button" onClick={onOpenAgent} className={activityToolbarButtonClass}>
            <ExternalLink size={12} aria-hidden /> Open agent
          </button>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" title="Close" className={drawerIconButtonClass}><X size={16} aria-hidden /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 px-4 pb-4">
            <MetaGrid cells={cells} />
            {failure && <div className="rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2.5 text-[12px] text-danger" role="alert">{failure}</div>}
            {error && <div className="text-[12px] text-danger" role="alert">{error}</div>}
            <SegmentedTabs ariaLabel="Run view" options={TABS} value={tab} onChange={(t) => { setTab(t); setNodeFocus(null); }} />
          </div>
          <div className="px-4 pb-6">
            {!exec && !error && <PageLoader label="Loading the run" />}
            {exec && tab === 'steps' && (
              <StepTimeline executionId={executionId} steps={exec.steps || []} definition={definition} runStatus={status} openNodeId={nodeFocus} />
            )}
            {exec && tab === 'graph' && (
              <div className="h-[420px] overflow-hidden rounded-md border border-[color:var(--border-default)]">
                {definition
                  ? <FlowCanvas definition={definition} stepByNodeId={stepByNodeId} mode="run" readOnly onNodeClick={(_e, n) => { setNodeFocus(n.id); setTab('steps'); }} />
                  : <p className="p-4 text-[12px] text-[color:var(--text-muted)]">This run has no graph.</p>}
              </div>
            )}
            {exec && tab === 'payload' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <JsonPane label="Trigger payload" value={exec.trigger_payload} />
                <JsonPane label="Output" value={exec.output} />
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
